import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
  DepositAmountResolver,
  DepositLifecycle,
  DepositRequirementState,
} from '@principle-theorem/principle-core';
import {
  DepositRequirementSource,
  type DepositBookingContext,
  type DepositCaptureStrategy,
  DepositRequestDeliveryChannel,
  DepositRequestOutcome,
  DepositRequestStatus,
  DepositPaymentMode,
  DepositStatus,
  type IAppointmentRequest,
  type ICreateDepositRequestParams,
  type ICreateDepositRequestResponse,
  type IDepositMetadata,
  type IDepositRequest,
  type IDepositSettings,
  type IDepositTreatmentConfigurationContribution,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  httpsCallable,
  serialise,
  snapshot,
  unserialise,
  type DayOfWeek,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, type Observable, from, of, throwError } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap,
  tap,
  timeoutWith,
} from 'rxjs/operators';
/**
 * Integer number of cents (e.g. 5000 = $50.00).
 *
 * All monetary values held or emitted by this store are cents. Convert to
 * dollars only at UI display / input boundaries via the accounting Money
 * helper.
 */
export type Cents = number;

export interface IDepositStoreState {
  depositFeatureEnabled: boolean;
  depositRequirement: DepositRequirementState;
  staffDepositOptIn: boolean;
  /** Deposit amount in {@link Cents} (integer). Undefined when not yet resolved. */
  depositAmount?: Cents;
  depositDefaultAmount?: Cents;
  depositSource?: DepositRequirementSource;
  depositWarning?: string;
  depositPaymentMode?: DepositPaymentMode;
  depositCaptureStrategy?: DepositCaptureStrategy;
  treatmentConfigurationDeposits?: Record<
    string,
    IDepositTreatmentConfigurationContribution
  >;
  treatmentConfigurationNames?: Record<string, string>;
  treatmentTemplateName?: string;
  patientName?: string;
  depositMetadata?: IDepositMetadata;
  appointmentRequest?: WithRef<IAppointmentRequest>;
  depositRequest?: WithRef<IDepositRequest>;
  /**
   * The channel of the send currently in flight, or undefined when idle.
   * Holding the channel rather than a bare flag is what lets a view spin only
   * the button that was pressed instead of every send button it renders.
   */
  paymentLinkSendingChannel?: DepositRequestDeliveryChannel;
  paymentLinkError?: string;
}

/**
 * Bounds an in-flight link send so `paymentLinkSendingChannel` always clears.
 *
 * Deposit surfaces derive "busy" from that channel, and the Take Deposit dialog
 * cannot be dismissed — a send that never settles would disable the deferral
 * button that is its only exit. This timeout is what keeps that exit reachable.
 */
const sendPaymentLinkTimeoutMs = 30_000;

const sendPaymentLinkTimeoutMessage =
  'Sending the payment link took too long. Please try again.';

const initialState: IDepositStoreState = {
  depositFeatureEnabled: false,
  depositRequirement: DepositRequirementState.Resolving,
  staffDepositOptIn: false,
  depositAmount: undefined,
  depositDefaultAmount: undefined,
  depositSource: undefined,
  depositWarning: undefined,
  depositPaymentMode: undefined,
  depositCaptureStrategy: undefined,
  treatmentConfigurationDeposits: undefined,
  treatmentConfigurationNames: undefined,
  treatmentTemplateName: undefined,
  patientName: undefined,
  depositMetadata: undefined,
  appointmentRequest: undefined,
  depositRequest: undefined,
  paymentLinkSendingChannel: undefined,
  paymentLinkError: undefined,
};

export interface IEvaluateDepositParams {
  brandSettings?: IDepositSettings;
  treatmentTemplateId?: string;
  treatmentConfigurationIds?: readonly string[];
  /**
   * Undefined while the patient's new-vs-existing status is still loading. The
   * store clears the resolved deposit rather than leaving the previously
   * selected patient's result on screen.
   */
  bookingContext?: DepositBookingContext;
  appointmentDay?: DayOfWeek;
  patientDepositRequired?: boolean;
  /** Treatment cost in {@link Cents} (integer). */
  treatmentCost?: Cents;
  /** Display names for {@link treatmentConfigurationIds}, keyed by the same id. */
  treatmentConfigurationNames?: Record<string, string>;
  treatmentTemplateName?: string;
  /** Display only. {@link patientDepositRequired} is what drives the decision. */
  patientName?: string;
}

export interface ISendPaymentLinkParams extends ICreateDepositRequestParams {
  /** Deposit amount to charge in {@link Cents} (integer). */
  amountCents: Cents;
  deliveryChannel: DepositRequestDeliveryChannel;
}

/**
 * Keeps persisted appointment deposit metadata aligned with its calculated
 * requirement, including contribution provenance and terminal status.
 */
export class AppointmentDepositMetadata {
  /**
   * Retains treatment configuration provenance only when it still accounts for
   * the recorded amount, preventing partial or overridden deposits from being
   * attributed to the wrong configurations.
   */
  static treatmentConfigurationDepositsForAmount(
    amount: Cents,
    deposits?: Record<string, IDepositTreatmentConfigurationContribution>
  ): Record<string, IDepositTreatmentConfigurationContribution> | undefined {
    const configuredTotal = Object.values(deposits ?? {}).reduce(
      (total, deposit) => total + deposit.amountCents * deposit.quantity,
      0
    );
    return configuredTotal === amount ? deposits : undefined;
  }

  /**
   * Preserves appointment deposit metadata when the recalculated requirement is
   * unchanged or the recorded status has already been settled.
   */
  static shouldRetainMetadata(
    metadata?: IDepositMetadata,
    amount?: Cents,
    treatmentConfigurationDeposits?: Record<
      string,
      IDepositTreatmentConfigurationContribution
    >
  ): boolean {
    return (
      AppointmentDepositMetadata._isTerminalDepositStatus(metadata?.status) ||
      (metadata?.amountCents === amount &&
        AppointmentDepositMetadata._sameTreatmentConfigurationDeposits(
          metadata?.treatmentConfigurationDeposits,
          treatmentConfigurationDeposits
        ))
    );
  }

  private static _sameTreatmentConfigurationDeposits(
    first?: Record<string, IDepositTreatmentConfigurationContribution>,
    second?: Record<string, IDepositTreatmentConfigurationContribution>
  ): boolean {
    const firstEntries = Object.entries(first ?? {});
    const secondEntries = Object.entries(second ?? {});
    return (
      firstEntries.length === secondEntries.length &&
      firstEntries.every(([configurationId, contribution]) => {
        const other = second?.[configurationId];
        return (
          other?.amountCents === contribution.amountCents &&
          other.quantity === contribution.quantity
        );
      })
    );
  }

  private static _isTerminalDepositStatus(status?: DepositStatus): boolean {
    return (
      status !== undefined &&
      DepositLifecycle.locksAppointmentDepositRequirement(status)
    );
  }
}

/** A named thing the deposit requirement can be traced back to. */
export interface IDepositRequirementContributor {
  name: string;
  quantity: number;
}

export interface IDepositDisplay {
  required: boolean;
  /** Deposit amount in {@link Cents} (integer). Undefined when not yet resolved. */
  amount?: Cents;
  source?: DepositRequirementSource;
  warning?: string;
}

@Injectable()
export class DepositStore extends ComponentStore<IDepositStoreState> {
  readonly deposit$ = this.select((state) => state);
  readonly depositFeatureEnabled$ = this.select(
    (state) => state.depositFeatureEnabled
  );
  readonly staffDepositOptIn$ = this.select((state) => state.staffDepositOptIn);
  readonly depositRequiredBySettings$ = this.select(
    (state) => state.depositRequirement === DepositRequirementState.Required
  );
  readonly depositRequiredOnlyBecauseStaffOptedIn$ = this.select(
    (state) =>
      state.staffDepositOptIn &&
      state.depositRequirement === DepositRequirementState.NotRequired
  );
  readonly depositRequirement$ = this.select((state) =>
    DepositStore._effectiveDepositRequirement(
      state.depositRequirement,
      state.staffDepositOptIn
    )
  );
  readonly depositRequired$ = this.select(
    this.depositRequirement$,
    (requirement) => requirement === DepositRequirementState.Required
  );
  readonly depositAmount$ = this.select((state) => state.depositAmount);
  readonly depositDefaultAmount$ = this.select(
    (state) => state.depositDefaultAmount
  );
  readonly depositSource$ = this.select((state) =>
    DepositStore._effectiveDepositSource(
      state.depositRequirement,
      state.staffDepositOptIn,
      state.depositSource
    )
  );
  readonly depositWarning$ = this.select((state) => state.depositWarning);
  readonly depositPaymentMode$ = this.select(
    (state) => state.depositPaymentMode
  );
  readonly depositCaptureStrategy$ = this.select(
    (state) => state.depositCaptureStrategy
  );
  /**
   * Withheld while the amount is overridden: the configurations that produced
   * the default no longer explain the amount. Restored on reset to default.
   */
  readonly treatmentConfigurationDeposits$ = this.select((state) =>
    state.depositAmount === state.depositDefaultAmount
      ? state.treatmentConfigurationDeposits
      : undefined
  );
  /**
   * What a saved deposit should remember about where it came from: the rule
   * that required it and whether staff changed the amount. Written onto the
   * metadata at save so the requirement can still be explained once the
   * settings behind it have moved on.
   */
  readonly depositOrigin$: Observable<
    Pick<IDepositMetadata, 'source' | 'amountOverridden'>
  > = this.select((state) => {
    const source = DepositStore._effectiveDepositSource(
      state.depositRequirement,
      state.staffDepositOptIn,
      state.depositSource
    );
    return {
      ...(source && source !== DepositRequirementSource.None ? { source } : {}),
      ...(state.depositAmount !== undefined &&
      state.depositDefaultAmount !== undefined &&
      state.depositAmount !== state.depositDefaultAmount
        ? { amountOverridden: true }
        : {}),
    };
  });
  readonly depositOverrideWarning$ = this.select((state) => {
    if (state.depositMetadata?.status === DepositStatus.Waived) {
      return 'You are overriding the default deposit settings by waiving this deposit.';
    }

    const amount = state.depositMetadata?.amountCents ?? state.depositAmount;
    if (
      amount !== undefined &&
      state.depositDefaultAmount !== undefined &&
      amount !== state.depositDefaultAmount
    ) {
      return 'You are overriding the default deposit amount for this appointment.';
    }
    return;
  });
  /**
   * Names what the deposit was required by, so staff can go and change the
   * right setting. Reads the resolved default contributions rather than
   * {@link treatmentConfigurationDeposits$}: overriding the amount changes what
   * is charged, not what made the deposit necessary.
   */
  readonly depositRequiredBy$ = this.select(
    (state): IDepositRequirementContributor[] => {
      if (state.depositSource === DepositRequirementSource.Patient) {
        return DepositStore._namedContributor(state.patientName);
      }
      if (state.depositSource === DepositRequirementSource.TreatmentTemplate) {
        return DepositStore._namedContributor(state.treatmentTemplateName);
      }
      if (
        state.depositSource !== DepositRequirementSource.TreatmentConfiguration
      ) {
        return [];
      }
      return DepositStore._configurationContributors(
        state.treatmentConfigurationDeposits,
        state.treatmentConfigurationNames
      );
    }
  );
  readonly depositMetadata$ = this.select((state) => state.depositMetadata);
  readonly appointmentRequest$ = this.select(
    (state) => state.appointmentRequest
  );
  readonly depositRequest$ = this.select((state) => state.depositRequest);
  readonly paymentLinkSendingChannel$ = this.select(
    (state) => state.paymentLinkSendingChannel
  );
  readonly paymentLinkSending$ = this.select(
    (state) => state.paymentLinkSendingChannel !== undefined
  );
  readonly paymentLinkError$ = this.select((state) => state.paymentLinkError);

  readonly depositDisplay$ = this.select(
    this.depositRequired$,
    this.depositAmount$,
    this.depositSource$,
    this.depositWarning$,
    (required, amount, source, warning): IDepositDisplay => ({
      required,
      amount,
      source,
      warning,
    })
  );

  readonly setDepositRequirement = this.updater(
    (
      state,
      deposit: {
        required: boolean;
        /** Deposit amount in {@link Cents} (integer). */
        amount?: Cents;
        source?: DepositRequirementSource;
        warning?: string;
        paymentMode?: DepositPaymentMode;
        captureStrategy?: DepositCaptureStrategy;
        treatmentConfigurationDeposits?: Record<
          string,
          IDepositTreatmentConfigurationContribution
        >;
        treatmentConfigurationNames?: Record<string, string>;
        treatmentTemplateName?: string;
        patientName?: string;
      }
    ) => {
      const shouldRetainMetadata =
        AppointmentDepositMetadata.shouldRetainMetadata(
          state.depositMetadata,
          deposit.amount,
          deposit.treatmentConfigurationDeposits
        );
      const retainedDepositMetadata =
        deposit.required && !shouldRetainMetadata
          ? undefined
          : state.depositMetadata;
      const linkedDepositRequestMetadata =
        retainedDepositMetadata === undefined
          ? DepositStore.metadataFromLinkedDepositRequest(
              state.appointmentRequest,
              state.depositRequest
            )
          : undefined;
      return {
        ...state,
        depositRequirement: deposit.required
          ? DepositRequirementState.Required
          : DepositRequirementState.NotRequired,
        depositAmount: deposit.amount,
        depositDefaultAmount: deposit.amount,
        depositSource: deposit.source,
        depositWarning: deposit.warning,
        depositPaymentMode: deposit.paymentMode,
        depositCaptureStrategy: deposit.captureStrategy,
        treatmentConfigurationDeposits: deposit.treatmentConfigurationDeposits,
        treatmentConfigurationNames: deposit.treatmentConfigurationNames,
        treatmentTemplateName: deposit.treatmentTemplateName,
        patientName: deposit.patientName,
        depositMetadata:
          retainedDepositMetadata ?? linkedDepositRequestMetadata,
      };
    }
  );

  readonly setDepositMetadata = this.updater(
    (state, metadata?: IDepositMetadata) => ({
      ...state,
      depositMetadata: metadata,
    })
  );

  readonly setStaffDepositOptIn = this.updater((state, optIn: boolean) => ({
    ...state,
    staffDepositOptIn: optIn,
    depositMetadata:
      !optIn &&
      DepositLifecycle.canDiscardStaffAddedMetadata(state.depositMetadata)
        ? undefined
        : state.depositMetadata,
  }));

  readonly setPersistedRequests = this.updater(
    (
      state,
      requests: {
        appointmentRequest?: WithRef<IAppointmentRequest>;
        depositRequest?: WithRef<IDepositRequest>;
      }
    ) => {
      const linkedDepositRequestMetadata =
        DepositStore.metadataFromLinkedDepositRequest(
          requests.appointmentRequest,
          requests.depositRequest
        );
      return {
        ...state,
        appointmentRequest: requests.appointmentRequest,
        depositRequest: requests.depositRequest,
        depositMetadata:
          state.depositMetadata ??
          linkedDepositRequestMetadata ??
          requests.appointmentRequest?.deposit?.metadata ??
          (requests.depositRequest
            ? DepositStore.metadataFromRequest(requests.depositRequest)
            : undefined),
      };
    }
  );

  readonly overrideAmount = this.updater((state, amount: Cents) => ({
    ...state,
    depositAmount: amount,
  }));

  readonly resetAmountToDefault = this.updater((state) => ({
    ...state,
    depositAmount: state.depositDefaultAmount,
  }));

  readonly setPaymentLinkSendingChannel = this.updater(
    (state, channel?: DepositRequestDeliveryChannel) => ({
      ...state,
      paymentLinkSendingChannel: channel,
    })
  );

  readonly setPaymentLinkError = this.updater((state, error?: string) => ({
    ...state,
    paymentLinkError: error,
  }));

  readonly evaluateDeposit = this.effect(
    (params$: Observable<IEvaluateDepositParams>) => {
      return params$.pipe(
        tap((params) => {
          if (!params.bookingContext) {
            this.patchState({
              depositFeatureEnabled: params.brandSettings?.enabled ?? false,
              depositRequirement: DepositRequirementState.Resolving,
              depositAmount: undefined,
              depositDefaultAmount: undefined,
              depositSource: undefined,
              depositWarning: undefined,
              depositPaymentMode: undefined,
              depositCaptureStrategy: undefined,
              treatmentConfigurationDeposits: undefined,
              treatmentConfigurationNames: undefined,
              treatmentTemplateName: undefined,
              patientName: undefined,
            });
            return;
          }

          const resolution = DepositAmountResolver.resolve({
            settings: params.brandSettings,
            treatmentTemplateId: params.treatmentTemplateId,
            treatmentConfigurationIds: params.treatmentConfigurationIds,
            bookingContext: params.bookingContext,
            appointmentDay: params.appointmentDay,
            patientDepositRequired: params.patientDepositRequired,
            treatmentCost: params.treatmentCost,
          });
          this.setDepositRequirement({
            required: resolution.required,
            amount: resolution.amount,
            source: resolution.source,
            warning: resolution.warning,
            paymentMode: resolution.paymentMode,
            captureStrategy: resolution.captureStrategy,
            treatmentConfigurationDeposits:
              resolution.treatmentConfigurationDeposits,
            treatmentConfigurationNames: params.treatmentConfigurationNames,
            treatmentTemplateName: params.treatmentTemplateName,
            patientName: params.patientName,
          });
          this.patchState({
            depositFeatureEnabled: params.brandSettings?.enabled ?? false,
          });
        })
      );
    }
  );

  /**
   * Seeds the store with the deposit already attached to an appointment
   * request, so a deposit paid during the online booking workflow (before the
   * appointment exists) is recognised when staff accept that request.
   *
   * The linked deposit request is fetched once rather than streamed: by the
   * time staff accept a request, its deposit status is terminal or will only
   * change through this workflow's own actions.
   */
  readonly hydrateFromAppointmentRequest = this.effect(
    (
      appointmentRequest$: Observable<WithRef<IAppointmentRequest> | undefined>
    ) =>
      appointmentRequest$.pipe(
        distinctUntilChanged(
          (previous, current) => previous?.ref.path === current?.ref.path
        ),
        switchMap(async (appointmentRequest) => {
          if (!appointmentRequest?.deposit) {
            return;
          }
          const requestRef = appointmentRequest.deposit.requestRef;
          const depositRequest = requestRef
            ? await Firestore.safeGetDoc(requestRef)
            : undefined;
          this.setPersistedRequests({ appointmentRequest, depositRequest });
        })
      )
  );

  readonly sendPaymentLink = this.effect(
    (params$: Observable<ISendPaymentLinkParams>) => {
      return params$.pipe(
        tap((params) => {
          this.setPaymentLinkSendingChannel(params.deliveryChannel);
          this.setPaymentLinkError(undefined);
        }),
        switchMap((params) =>
          from(
            snapshot(
              httpsCallable<
                SerialisedData<ICreateDepositRequestParams>,
                SerialisedData<ICreateDepositRequestResponse>
              >('http-stripe-createDepositRequest')(serialise(params))
            )
          ).pipe(
            timeoutWith(
              sendPaymentLinkTimeoutMs,
              throwError(new Error(sendPaymentLinkTimeoutMessage))
            ),
            switchMap((serialisedResponse) => {
              const response: ICreateDepositRequestResponse =
                unserialise(serialisedResponse);
              return combineLatest([
                Firestore.doc$(response.depositRequestRef),
                response.appointmentRequestRef
                  ? Firestore.doc$(response.appointmentRequestRef)
                  : of(undefined),
              ]).pipe(
                map(([depositRequest, appointmentRequest]) => ({
                  response,
                  depositRequest,
                  appointmentRequest,
                }))
              );
            }),
            tapResponse(
              ({ response, depositRequest, appointmentRequest }) => {
                this.setPaymentLinkSendingChannel(undefined);
                this.setPersistedRequests({
                  appointmentRequest,
                  depositRequest,
                });
                this.setPaymentLinkError(
                  response.deliveryStatus === DepositRequestOutcome.Failed
                    ? response.deliveryError ??
                        'The payment link was created but could not be sent'
                    : undefined
                );
              },
              (error: { message?: string }) => {
                this.setPaymentLinkSendingChannel(undefined);
                this.setPaymentLinkError(
                  error.message ?? 'Failed to send payment link'
                );
              }
            )
          )
        )
      );
    }
  );

  constructor() {
    super(initialState);
  }

  /**
   * Converts the persisted deposit request into appointment metadata so the
   * backend money lifecycle remains the canonical status. Staff attention is
   * mirrored as well, so the scheduling sidebar flags the same deposits the
   * requests table does rather than showing a clean chip for a flagged one.
   */
  static metadataFromRequest(
    depositRequest: WithRef<IDepositRequest>
  ): IDepositMetadata {
    const statusMap: Record<DepositRequestStatus, DepositStatus> = {
      [DepositRequestStatus.Pending]: DepositStatus.Pending,
      [DepositRequestStatus.Authorised]: DepositStatus.Authorised,
      [DepositRequestStatus.Paid]: DepositStatus.Paid,
      [DepositRequestStatus.Expired]: DepositStatus.Cancelled,
      [DepositRequestStatus.Cancelled]: DepositStatus.Cancelled,
      [DepositRequestStatus.Released]: DepositStatus.Released,
      [DepositRequestStatus.Refunded]: DepositStatus.Refunded,
    };
    return {
      status: statusMap[depositRequest.status],
      amountCents: depositRequest.amountCents,
      currency: depositRequest.currency,
      paymentMode: depositRequest.paymentMode,
      ...(depositRequest.captureStrategy
        ? { captureStrategy: depositRequest.captureStrategy }
        : {}),
      ...(depositRequest.fallbackReason
        ? { fallbackReason: depositRequest.fallbackReason }
        : {}),
      ...(depositRequest.treatmentConfigurationDeposits
        ? {
            treatmentConfigurationDeposits:
              depositRequest.treatmentConfigurationDeposits,
          }
        : {}),
      ...(depositRequest.paymentAttemptRef
        ? { paymentAttemptRef: depositRequest.paymentAttemptRef }
        : {}),
      ...(depositRequest.paidAt ? { paidAt: depositRequest.paidAt } : {}),
      ...(depositRequest.captureBefore
        ? { captureBefore: depositRequest.captureBefore }
        : {}),
      ...(depositRequest.staffAttention
        ? { attention: depositRequest.staffAttention }
        : {}),
    };
  }

  /**
   * Returns request-derived metadata only when the deposit request is linked to
   * the appointment request being accepted.
   */
  static metadataFromLinkedDepositRequest(
    appointmentRequest?: WithRef<IAppointmentRequest>,
    depositRequest?: WithRef<IDepositRequest>
  ): IDepositMetadata | undefined {
    if (!appointmentRequest || !depositRequest) {
      return;
    }

    if (
      !DepositStore._isLinkedDepositRequest(appointmentRequest, depositRequest)
    ) {
      return;
    }

    return DepositStore.metadataFromRequest(depositRequest);
  }

  private static _namedContributor(
    name?: string
  ): IDepositRequirementContributor[] {
    return name ? [{ name, quantity: 1 }] : [];
  }

  private static _configurationContributors(
    deposits?: Record<string, IDepositTreatmentConfigurationContribution>,
    names?: Record<string, string>
  ): IDepositRequirementContributor[] {
    return Object.entries(deposits ?? {}).flatMap(
      ([configurationId, contribution]) => {
        const name = names?.[configurationId];
        return name ? [{ name, quantity: contribution.quantity }] : [];
      }
    );
  }

  private static _isLinkedDepositRequest(
    appointmentRequest: WithRef<IAppointmentRequest>,
    depositRequest: WithRef<IDepositRequest>
  ): boolean {
    return (
      appointmentRequest.deposit?.requestRef?.path ===
        depositRequest.ref.path ||
      depositRequest.appointmentRequestRef?.path === appointmentRequest.ref.path
    );
  }

  private static _effectiveDepositRequirement(
    requirement: DepositRequirementState,
    staffDepositOptIn: boolean
  ): DepositRequirementState {
    if (
      staffDepositOptIn &&
      requirement === DepositRequirementState.NotRequired
    ) {
      return DepositRequirementState.Required;
    }
    return requirement;
  }

  private static _effectiveDepositSource(
    requirement: DepositRequirementState,
    staffDepositOptIn: boolean,
    source?: DepositRequirementSource
  ): DepositRequirementSource | undefined {
    if (
      staffDepositOptIn &&
      requirement === DepositRequirementState.NotRequired
    ) {
      return DepositRequirementSource.Staff;
    }
    return source;
  }

  reset(): void {
    this.setState(initialState);
  }
}
